import { Box, Skeleton, Stack, Theme, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { ColoredIcon, NoDataFound, StatusChip } from '@verifime/components';
import { formatToDDMMMYYYY, SUPPORTED_COUNTRY_MAP } from '@verifime/utils';
import NameValueDisplay from './NameValueDisplay';

/**
 * What a cell shows when the document does not carry that field.
 *
 * **A dash, where `KYC` shows "N/A".** The divergence is deliberate and is the reason this file
 * exists as a copy: the rest of this feature's surface already uses a dash for an absent value,
 * including the check cards' timestamp and `EvidenceProvenance`, and one table spelling it
 * differently reads as a different kind of absence rather than a house style. `KYC` is still
 * rendered by the organisation customer page (`OrganisationCustomerDetails`, via
 * `VerificationDetails`'s `KYB`), and it keeps "N/A" there.
 */
const MISSING_VALUE = '-';

/** How many placeholder rows the loading table draws. */
const LOADING_ROW_COUNT = 3;

/**
 * What the mark on a document says, and the only thing it is allowed to say.
 *
 * One sentence, used verbatim as both the icon's accessible name and its tooltip. It is deliberately
 * silent on why: the row is told which documents are behind the level and nothing else, because we
 * are not permitted to tell a client which government check failed. Written in
 * ASD Simplified Technical English and approved as this exact sentence, so it is not to be reworded,
 * extended, or joined to a second sentence.
 */
const DECREASES_CONFIDENCE_LABEL = 'This document decreases the confidence level.';

/**
 * How far above the centre of its line box a line of this table's text actually centres its ink.
 *
 * A line box reserves room under the baseline for descenders whether or not the text has any, so
 * its centre sits below the centre of the glyphs, and a document type carries few descenders and
 * often none at all. An icon centred on the line box therefore reads as sitting low beside the
 * type, which is the whole of the difference a reader notices.
 *
 * Measured in Chrome against this table's own text, Outfit at the grid's 14px over a 16.8px line:
 * the baseline sits 13.00px down the line box, which puts the line box's centre 4.60px above it,
 * while `measureText` puts the ink of a type name from 10.09px above the baseline to 0.15px below
 * it, centring it 4.97px above. The 0.37px between the two is this value.
 *
 * It survives a change of line height, because the baseline's distance from the line box's centre
 * is half the difference between the font's ascent and its descent whatever the leading is. It does
 * not survive a change of typeface, of the grid's font size, or of the weight the mark sets the type
 * in, because each of those moves the ink. The `MarkSitsOnTheTypeTextsOpticalCentre` story re-derives
 * the whole measurement in the browser and fails if any of them does.
 */
const GLYPH_BAND_RISE = '0.37px';

/** Renders a value, or a dash where there is none. Empty strings count as none. */
function orDash(value?: string): string {
  return value ? value : MISSING_VALUE;
}

/**
 * Formats a date, or a dash where there is none.
 *
 * Not `formatToDDMMMYYYY` alone: its own fallback for an absent value is "N/A", which would put
 * the old spelling back into a table that has otherwise moved to the dash.
 */
function formatDateOrDash(value?: string): string {
  return value ? formatToDDMMMYYYY(value) : MISSING_VALUE;
}

/**
 * The country's full name, from the code the scan result carries.
 *
 * The API returns an ISO code (`AUS`), and a client reading a compliance record should see the
 * country. `SUPPORTED_COUNTRY_MAP` already holds both the alpha-2 and alpha-3 spellings against one
 * label, so this needs no reference lookup and no request.
 *
 * **An unrecognised code renders as itself, not as a dash.** A code we cannot expand is still
 * something the document said, and replacing it with a dash would claim the document named no
 * country when it named one we do not have a label for. The dash is reserved for genuinely absent.
 */
function countryName(code?: string): string {
  if (!code) {
    return MISSING_VALUE;
  }

  return SUPPORTED_COUNTRY_MAP[code as keyof typeof SUPPORTED_COUNTRY_MAP]?.label ?? code;
}

/**
 * A deliberate duplicate of `KYC.tsx`, not a refactor of it.
 *
 * Individuals reach this component through `ConfidenceCustomerPage`'s `KycVerificationSectionView`;
 * organisations still reach the plain `KYC` component through `VerificationDetails`'s `KYB` on the
 * organisation customer page. The two are kept apart because they spell an absent value differently
 * on purpose (see `MISSING_VALUE` above): sharing a helper between this component and `KYC` would
 * push that spelling onto whichever table does not already use it, and that is a visible change to
 * the organisation table that needs its own decision, not something this file gets to make.
 *
 * Retiring the duplication starts with that decision on the organisation table's absent-value
 * spelling. Once it is made, `KYC.tsx` either gains these columns directly or is replaced by this
 * component, and whichever happens, delete this file (and revert `index.ts`'s export of it) as part
 * of that change.
 *
 * One label/value pair for a name, e.g. `{ label: 'Given names', value: 'JANE ELIZABETH' }`. A list
 * rather than fixed fields because what a document type shows varies: a passport folds any middle
 * name into "Given names" and prints two parts, matching what DVS genuinely checks against it, while
 * a driver licence and a Medicare card each print one whole name under the single label `'Name'`.
 */
export type TExtractedNamePart = {
  label: string;
  value: string;
};

/**
 * How the `extractedName` column renders. `'pairs'` lays each label/value part out side by side.
 * Only one mode exists today; the type stays a union so a future display mode can be added
 * additively without touching this one's meaning.
 */
export type TKycNameDisplay = 'pairs';

export type TKycWithDocumentNamesRow = {
  id: number;
  identitySource?: string;
  /** The kind of document, e.g. 'Driver Licence'. Additive: distinct from `identitySource`, which
   * existing call sites already populate with the same kind of value under a different column. */
  documentType?: string;
  expires?: string;
  countryOfIssue?: string;
  lastUpdated?: string;
  /** The name this document printed, as label/value pairs. Only rendered when the `extractedName`
   * column is requested via `columnNames` and `nameDisplay="pairs"`. */
  extractedName?: TExtractedNamePart[];
  /**
   * Marks the document as past its expiry date, which puts an "Expired" chip beside the date and
   * turns the whole row's text to the error colour.
   *
   * Typed as the single literal the component acts on rather than a general status string. This is
   * all that is left of the old status vocabulary: the DVS-derived values were removed because the
   * audit finding forbids DVS-specific status anywhere client-facing, and expiry is a property of
   * the document itself rather than of any government check.
   *
   * It was previously read off the row without being declared here at all, so the expired
   * treatment could not be reached through the public type and its story silently rendered an
   * empty table.
   */
  status?: 'Expired';
  /**
   * Marks this document as one of those behind the aggregate document confidence level, which puts
   * a warning icon before the document type and sets that text in the danger tone at weight 600.
   *
   * **A bare boolean, and deliberately nothing richer.** The confidence response names the document
   * types causing the level and carries no reason, because we are not permitted to tell a client
   * which government check failed. A flag cannot carry one by construction; a status, a message or a
   * source field could, so none of those is offered here.
   *
   * Decorates the `documentType` cell only, the way `status` decorates `expires`. It is never
   * selectable through `columnNames`, and a caller that does not request `documentType` sees no mark,
   * for the same reason a caller that does not request `expires` sees no "Expired" chip.
   */
  decreasesConfidence?: boolean;
};
export type TKycWithDocumentNamesRows = TKycWithDocumentNamesRow[];
// `status` and `decreasesConfidence` are excluded alongside `id` because none of them is a column.
// Each decorates a cell that is selected in its own right: `status` the `expires` cell,
// `decreasesConfidence` the `documentType` cell. Neither is ever selectable through `columnNames`.
type TColumnMapping = {
  [key in keyof Omit<
    Required<TKycWithDocumentNamesRow>,
    'id' | 'status' | 'decreasesConfidence'
  >]: GridColDef;
};

/**
 * Lays out a document's extracted name parts side by side, each as a label above its value.
 *
 * **Values display in upper case, via CSS `text-transform: uppercase` on the value only.** The
 * transform is applied through `valueSx` on `NameValueDisplay`, which reaches the value
 * `Typography` and nothing else, so `part.label` (`Given names`, `Family name`, and so on) keeps
 * its own casing untouched.
 *
 * Upper case rather than title case: casing cannot be recovered from a document scan, so we do
 * not know whether the scanner already emits capitals for a given field. Upper-casing invents
 * nothing either way, whereas title-casing would guess at an internal capital the scan never
 * confirmed, corrupting a name like "McKeough" into "Mckeough".
 *
 * The transform lives here, in the display layer, rather than in `documentRows.ts`: it only
 * changes how the value paints on screen. The underlying string, what a client would copy out of
 * the cell, and what a screen reader without its own case-normalisation would announce, stays
 * exactly what the document printed.
 */
function NameOnDocumentCell({ parts }: Readonly<{ parts?: TExtractedNamePart[] }>) {
  const theme = useTheme();

  // A dash rather than an empty cell. A document whose name could not be read, and a document type
  // that prints no name at all, both land here, and an empty cell says neither: it reads as a
  // rendering gap rather than as "this column has no value for this row".
  if (!parts || parts.length === 0) {
    return MISSING_VALUE;
  }

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      // Row-gap 2px, column-gap 32px: a wide column collapses the parts onto one line 2px apart,
      // a narrow one wraps them onto their own lines with the wider 32px still separating columns.
      // `gap={...}` alone only takes one value, so this needs the two-value shorthand via `sx`.
      sx={{ gap: `${theme.semantic.spacing['3xs']} ${theme.semantic.spacing.xl}` }}
    >
      {parts.map((part) => (
        <NameValueDisplay
          key={part.label}
          name={part.label}
          value={part.value}
          flex="none"
          valueSx={{ textTransform: 'uppercase' }}
        />
      ))}
    </Stack>
  );
}

/**
 * The document type cell, marked when this document is one of those behind the confidence level.
 *
 * **Only the type cell is marked, and only ever with an icon, a colour and a weight.** The design is
 * explicit that the rest of the row is left alone: the other cells hold what the customer confirmed
 * and what the record holds, and marking them would make a wider claim than the one being made.
 * There is no row background, no border, and no reason text anywhere, because the contract behind
 * this deliberately carries no reason.
 *
 * **`warningAmberRounded`, the icon the repo already has, rather than a new unrounded one.** The
 * design's own SVG is MUI's unrounded `WarningAmber`, but the difference at this size is the corner
 * radius on the triangle and the exclamation bar, and every other status icon this product draws is
 * from the Rounded set (`warningRounded`, `errorRounded`, `checkCircleRounded`). This exact icon
 * already carries the same "needs attention" meaning elsewhere in the portal. A single unrounded
 * warning would be the one icon in the vocabulary that does not match its neighbours.
 *
 * **The colour is `text.danger`, not `error.main`.** `error.main` is a fill colour, chosen to carry
 * white text rather than to be read against paper, and it lands near 2.4:1 on the dark theme's
 * paper. `text.danger` is the token for text and inline icons drawn straight onto a surface, and it
 * already resolves to the two steps the design settled on: Red 500 in light and Red 300 in dark,
 * the latter precisely because the light step falls to 3.36:1 against dark paper and fails AA. The
 * `highlight-row-text` rule further down still reaches for `error.main`; that line is a standing
 * ADR 78 deviation and is not a pattern to copy.
 *
 * **One box in both states, with the icon drawn out of flow.** The grid centres a cell's content
 * vertically, so a marked cell whose content is taller than one line of text has that text lifted
 * off the line the rest of its row sits on. The icon is 18px against the text's 16.8px line box, so
 * in flow it is the taller of the two and it decides the box the grid centres: that is what put a
 * marked document type 1px above its own row's other cells. Positioning the icon absolutely leaves
 * the box exactly one line of text tall, marked or not, so the mark cannot move the text at all,
 * and the room it needs is reserved with a padding derived from the icon's own size and the gap it
 * is set at rather than restated as a third number. Offset down from the top of that box rather
 * than centred in it, the icon meets the first line of the type rather than the middle of the text
 * as a whole. Nothing in this table wraps today: the grid sets `white-space: nowrap` on its cells,
 * so a type name too long for its column is clipped rather than broken over two lines. The mark
 * does not rely on that staying true.
 *
 * **On that line, the icon centres on the type's ink rather than on its line box.** The two are not
 * the same place: the line box keeps room under the baseline for descenders the type does not have,
 * so its centre sits below the centre of the glyphs and an icon centred on it reads as low. See
 * `GLYPH_BAND_RISE` for the measurement that separates them.
 */
function DocumentTypeCell({
  label,
  decreasesConfidence,
}: Readonly<{ label: string; decreasesConfidence?: boolean }>) {
  const theme = useTheme();

  return (
    <Box
      data-component={decreasesConfidence ? 'decreases-confidence-marker' : undefined}
      sx={{
        position: 'relative',
        ...(decreasesConfidence && {
          color: theme.palette.text.danger,
          fontWeight: theme.semantic.fontWeight.semibold,
          paddingLeft: `calc(${theme.semantic.fontSize.lg} + ${theme.semantic.spacing.xs})`,
        }),
      }}
    >
      {decreasesConfidence && (
        <Tooltip title={DECREASES_CONFIDENCE_LABEL} describeChild placement="top">
          {/* A wrapper element, because a tooltip does not activate on hover for a child that takes
              no user interaction of its own. See https://mui.com/material-ui/react-tooltip/#disabled-elements. */}
          <Box
            component="span"
            sx={{
              display: 'flex',
              position: 'absolute',
              left: 0,
              // Centred on the ink of the first line of the type, measured from the top of the box
              // rather than from its middle so it stays on the first line however many the box
              // grows to hold. Half the line box less half the icon centres an 18px icon on a
              // 16.8px line, and the rise then lifts it off the line box's centre onto the glyphs'.
              // The line box is the grid's own: MUI X sets the grid from `typography.body2`, which
              // is these two tokens.
              top: `calc((${theme.semantic.fontSize.sm} * ${theme.semantic.lineHeight.short} - ${theme.semantic.fontSize.lg}) / 2 - ${GLYPH_BAND_RISE})`,
            }}
          >
            <ColoredIcon
              iconName="warningAmberRounded"
              // `role`/`aria-hidden` together, because MUI hides an icon from assistive technology by
              // default. Without them the mark would be visible and unannounced.
              role="img"
              aria-hidden={false}
              aria-label={DECREASES_CONFIDENCE_LABEL}
              sx={{
                // Neither grows nor shrinks: `ColoredIcon` lets an icon flex by default.
                flex: 'none',
                fill: theme.palette.text.danger,
                fontSize: theme.semantic.fontSize.lg,
              }}
            />
          </Box>
        </Tooltip>
      )}
      {label}
    </Box>
  );
}

export default function KycWithDocumentNames({
  rows,
  columnNames,
  // 'pairs' reproduces the only rendering `extractedName` has ever had, so leaving this prop out
  // is indistinguishable from passing it: the column only appears when a caller adds
  // `extractedName` to `columnNames`, which none of the existing call sites do.
  nameDisplay = 'pairs',
  isLoading = false,
  'data-testid': dataTestId,
}: Readonly<{
  rows: TKycWithDocumentNamesRows;
  columnNames: (keyof TColumnMapping)[];
  nameDisplay?: TKycNameDisplay;
  /**
   * Draws the table as placeholder rows while the documents are being fetched.
   *
   * Deliberately not the same thing as an empty `rows`. An empty table renders "No data found",
   * which is a statement about the customer: they hold no identity documents. Showing that while
   * the request is still in flight makes a claim we cannot support yet, and one a client could act
   * on. `isLoading` is what keeps the two apart.
   */
  isLoading?: boolean;
  'data-testid'?: string;
}>) {
  const theme = useTheme();

  const getRowClassName = (params: GridRowParams) => {
    return params.row.status === 'Expired' ? 'highlight-row-text' : '';
  };

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const flex = isDesktop && 1;
  // The prototype gives the name column twice the flex weight of its sibling columns (measured at
  // its own table width: a 340.66px name column against a 170.33px shared unit on the other four),
  // not an equal share. That extra width is what lets a multi-part name, e.g. a passport's given
  // names and family name, sit on one line: at an equal share, the same fixture wraps. Matching the
  // ratio, not just the gap and font-size numbers, keeps that true regardless of how wide the table
  // ends up. A driver licence no longer needs the extra room for this reason since it now joins to
  // one value, but the column keeps the same width so every row lines up.
  const nameColumnFlex = isDesktop && 2;
  const MAPPINGS: TColumnMapping = {
    identitySource: {
      field: 'identitySource',
      headerName: 'Identity source',
      flex,
      sortable: false,
      renderCell: ({ value }) => orDash(value),
    },
    documentType: {
      field: 'documentType',
      // Sentence case throughout, including 'Country of issue' and 'Last updated' below.
      //
      // The casing was transcribed from a mock of the pre-existing `KYC` component, which carries
      // no data story of its own, and this component is new work, so the agreed design binds
      // rather than the mock it was cloned from. See verifime-adr#264.
      headerName: 'Document type',
      flex,
      sortable: false,
      renderCell: ({ value, row }) => (
        <DocumentTypeCell label={orDash(value)} decreasesConfidence={row.decreasesConfidence} />
      ),
    },
    countryOfIssue: {
      field: 'countryOfIssue',
      headerName: 'Country of issue',
      flex,
      sortable: false,
      // The row carries the ISO code the scan returned; the client reads the country.
      renderCell: ({ value }) => countryName(value),
    },
    extractedName: {
      field: 'extractedName',
      // 'Confirmed by the customer', not 'Name on document': every value in this column is the
      // customer's own page-1 answer, not a document scanner's reading. That was true even under
      // the old header, and it is true regardless of what DVS itself checks: today DVS still
      // receives and checks all three of a driver licence's name parts. person-api#1845 puts the
      // middle name behind a `driver-licence-middle-name-suppression.enabled` toggle, defaulted
      // off, so DVS keeps receiving it until that toggle is enabled. Either way, labelling the
      // column as document-derived implied a validation the document itself never underwent.
      headerName: 'Confirmed by the customer',
      flex: nameColumnFlex,
      sortable: false,
      renderCell: ({ value }) =>
        nameDisplay === 'pairs' ? <NameOnDocumentCell parts={value} /> : null,
    },
    expires: {
      field: 'expires',
      headerName: 'Expires',
      flex,
      sortable: false,
      renderCell: ({ value, row }) => {
        if (row.status === 'Expired') {
          return (
            <Stack
              direction="row"
              alignItems="center"
              sx={(t: Theme) => ({ gap: t.semantic.spacing['2xs'] })}
            >
              {formatDateOrDash(value)} <StatusChip status="Expired" />
            </Stack>
          );
        }
        return formatDateOrDash(value);
      },
    },
    lastUpdated: {
      field: 'lastUpdated',
      headerName: 'Last updated',
      flex,
      sortable: false,
      renderCell: ({ value }) => formatDateOrDash(value),
    },
  };

  if (columnNames.length === 0) {
    throw new Error('Column specified cannot be empty.');
  }
  const columns = columnNames.map((columnMapping) => MAPPINGS[columnMapping]);

  // Skeleton rows rather than the grid's own loading overlay. This grid runs `autoHeight`, so with
  // no rows there is nothing for an overlay to sit inside and the table collapses to its header:
  // the page jumps by the height of the whole table when the documents land. Placeholder rows hold
  // the real geometry, columns included, so only the text inside them changes.
  //
  // Negative ids so a placeholder can never collide with a real row's id, which is positional and
  // starts at 1.
  const loadingRows: TKycWithDocumentNamesRows = Array.from(
    { length: LOADING_ROW_COUNT },
    (_, index) => ({ id: -(index + 1) }),
  );
  const loadingColumns = columns.map((column) => ({
    ...column,
    renderCell: () => <Skeleton variant="text" width="70%" />,
  }));

  return (
    <DataGrid
      // `aria-busy` for the same reason the check cards carry it: without it a screen reader hears
      // a table of empty cells, which is indistinguishable from a customer holding no documents.
      aria-busy={isLoading}
      rows={isLoading ? loadingRows : rows}
      columns={isLoading ? loadingColumns : columns}
      hideFooter
      disableColumnFilter
      disableColumnSelector
      disableColumnMenu
      disableDensitySelector
      disableVirtualization
      disableRowSelectionOnClick
      autoHeight
      getRowClassName={getRowClassName}
      data-testid={dataTestId}
      sx={{
        border: '0',
        '& .highlight-row-text': {
          color: (theme: Theme) => theme.palette.error.main,
        },
        // Without this the grid sets its headings in the same face as its cells, and the table
        // reads as one undifferentiated block. A shaded band rather than only a heavier face:
        // weight alone left the headings sitting on the card's own white, so the table opened
        // with no visible boundary between its labels and its first row of data.
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: (theme: Theme) => theme.palette.background.default,
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontSize: (theme: Theme) => theme.semantic.fontSize.xs,
          fontWeight: (theme: Theme) => theme.semantic.fontWeight.semibold,
          // Not text.secondary. A heading that is quieter than the value under it reads as a
          // caption on the row rather than as the column's name.
          color: (theme: Theme) => theme.palette.text.primary,
        },
      }}
      slots={{
        noRowsOverlay: NoDataFound,
      }}
    />
  );
}
